<script setup>
import { computed } from 'vue';
import Icon from 'next/icon/Icon.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  // eslint-disable-next-line vue/no-unused-properties
  active: {
    type: Boolean,
    default: false,
  },
  inbox: {
    type: Object,
    required: true,
  },
});

const reauthorizationRequired = computed(() => {
  return props.inbox.reauthorization_required;
});

const isWhatsappInbox = computed(() => {
  return props.inbox.channel_type === INBOX_TYPES.WHATSAPP;
});

const connectionStatus = computed(() => props.inbox.connection_status);

const showConnectionDot = computed(() => isWhatsappInbox.value);

const connectionTooltip = computed(() => {
  if (connectionStatus.value === 'connected') return 'Conectado';
  if (connectionStatus.value === 'connecting') return 'Conectando...';
  if (connectionStatus.value === 'disconnected') return 'Desconectado';
  return 'Status desconhecido';
});

const dotStyle = computed(() => {
  const status = connectionStatus.value;
  if (status === 'connected') {
    return {
      backgroundColor: '#22c55e',
      boxShadow: '0 0 0 1px #fff, 0 0 4px 1px #22c55e',
    };
  }
  if (status === 'connecting') {
    return {
      backgroundColor: '#f59e0b',
      boxShadow: '0 0 0 1px #fff, 0 0 4px 1px #f59e0b',
    };
  }
  if (status === 'disconnected') {
    return {
      backgroundColor: '#ef4444',
      boxShadow: '0 0 0 1px #fff',
    };
  }
  return {
    backgroundColor: '#4b5563',
    boxShadow: '0 0 0 1px #fff',
  };
});

const dotAnimationClass = computed(() => {
  if (connectionStatus.value === 'connected') return 'dot-pulse-green';
  if (connectionStatus.value === 'connecting') return 'dot-pulse-yellow';
  return '';
});
</script>

<template>
  <span class="size-4 grid place-content-center rounded-full relative">
    <ChannelIcon :inbox="inbox" class="size-4" />
    <span
      v-if="showConnectionDot"
      v-tooltip.top-end="connectionTooltip"
      class="connection-dot"
      :class="dotAnimationClass"
      :style="dotStyle"
    />
  </span>
  <div class="flex-1 truncate min-w-0">{{ label }}</div>
  <div
    v-if="reauthorizationRequired"
    v-tooltip.top-end="$t('SIDEBAR.REAUTHORIZE')"
    class="grid place-content-center size-5 bg-n-ruby-5/60 rounded-full"
  >
    <Icon icon="i-woot-alert" class="size-3 text-n-ruby-9" />
  </div>
</template>

<style scoped>
.connection-dot {
  position: absolute;
  bottom: -1px;
  right: -1px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
}

@keyframes pulse-green {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.2);
  }
}

@keyframes pulse-yellow {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

.dot-pulse-green {
  animation: pulse-green 2s ease-in-out infinite;
}

.dot-pulse-yellow {
  animation: pulse-yellow 1s ease-in-out infinite;
}
</style>
